<template>
  <div class="sovi-pricing bg-gradient-dark">
    <div class="sovi-pricing__wrapper">
      <h2 class="home-title mb-3 text-center font-bold">
        {{ companyPricing ? t('companyPricing') : t('consumerPricing') }}
      </h2>

      <div class="sovi-pricing__cards pt-2">
        <div
          v-for="item in packageItems"
          :key="item.id"
          class="sovi-pricing__card"
        >
          <div class="sovi-pricing__card__content">
            <div class="sovi-pricing__card__title-wrapper">
              <v-icon class="sovi-pricing__card__icon" :icon="item.icon" />

              <span class="sovi-pricing__card__title">
                Sovi {{ item.signatures }}
              </span>
            </div>

            <p v-if="companyPricing" class="sovi-pricing__card__price">
              {{ formatCurrency(item.price / (+vat / 100 + 1)) }}
              <span class="caption">{{ t('noVat') }}</span>
            </p>

            <p v-else class="sovi-pricing__card__price">
              {{ formatCurrency(item.price) }}
              <span class="caption">{{ t('incVat') }}</span>
            </p>

            <p
              class="sovi-pricing__card__subtitle"
              v-html="
                t('includes', { amount: item.signatures }, item.signatures)
              "
            />

            <div class="sovi-pricing__card__unit-price">
              {{ getUnitPrice(item) }} / {{ t('pcs') }}
            </div>
          </div>
        </div>
      </div>

      <p class="text-center">
        {{ companyPricing ? t('doesNotIncludeVat') : t('includesVat') }}
      </p>

      <v-btn
        v-if="!companyPricing"
        color="tertiary"
        class="text-none"
        @click="companyPricing = !companyPricing"
      >
        <v-icon left icon="mdi-account-group" />
        {{ t('showCompanyPricing') }}
      </v-btn>

      <v-btn
        v-else
        class="text-none"
        color="tertiary"
        @click="companyPricing = !companyPricing"
      >
        <v-icon left icon="mdi-account" />
        {{ t('showConsumerPricing') }}
      </v-btn>

      <p class="my-0 text-center text-sm">
        <a
          target="_blank"
          rel="noopener noreferrer"
          class="font-semibold text-tertiary-500 decoration-white underline-offset-4 hover:underline"
          href="/pdf/Sovi_toimitus_ja_maksuehdot.pdf"
          @click.stop
        >
          {{ t('paymentTerms') }}
        </a>
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { usePackageStore } from '@/stores/package';
import { formatCurrency } from '@/utils';
import type { Package } from '@/types/package';
import { useI18n } from 'vue-i18n';

const companyPricing = ref(true);

const { getPackages } = usePackageStore();

const { packages, vat } = storeToRefs(usePackageStore());

const { t } = useI18n();

const packageItems = computed(() => {
  const icons = {
    1: 'mdi-account',
    2: 'mdi-account-multiple',
    3: 'mdi-account-group',
  };

  return packages.value.map((p) => ({
    ...p,
    icon: icons[p.id as keyof typeof icons] || 'mdi-account-group',
  }));
});

const getUnitPrice = (item: Package) =>
  `${formatCurrency(
    (companyPricing.value ? item.price / (+vat.value / 100 + 1) : item.price) /
      item.signatures,
  )}`;

onMounted(async () => {
  await getPackages();
});
</script>

<style lang="scss">
.sovi-pricing {
  --icon-color: rgb(var(--v-theme-primary));
  --title-size: 1.75rem;

  color: rgb(255 255 255 / 87%);
  padding: clamp(40px, 5vw, 92px);
  line-height: 1;

  &__cards {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: clamp(8px, 8vw, 38px);
    flex-wrap: wrap;
    margin-bottom: 16px;
  }

  &__wrapper {
    max-width: 1280px;
    margin: 0 auto;
    display: grid;
    gap: clamp(16px, 5vw, 32px);
  }

  &__card {
    position: relative;
    isolation: isolate;
    border-radius: 16px;

    &__title-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &__title {
      font-size: 1.5rem;
      text-transform: uppercase;
    }

    &__content {
      display: grid;
      grid-template-columns: 1fr;
      max-width: 400px;
      padding: clamp(24px, 2vw, 48px);
      gap: clamp(16px, 2vw, 24px);
      border-radius: inherit;
      background-color: #191919;
    }

    &::before,
    &::after {
      content: '';
      position: absolute;
      background-color: var(--icon-color);
      height: 90%;
      width: 90%;
      bottom: 0;
      right: 0;
      border-radius: inherit;
      z-index: -1;
    }

    &::before {
      opacity: 0.25;
      translate: 8px 16px;
    }

    &::after {
      opacity: 0.75;
      translate: 4px 8px;
      height: 95%;
      width: 95%;
    }

    &__price {
      font-size: var(--title-size);

      // @include mq($from: md) {
      //   --title-size: 2.5rem;
      // }
    }

    .v-icon {
      color: var(--icon-color);
      font-size: 2.125rem;
      border-radius: 50%;
    }

    &:nth-child(2) {
      --icon-color: rgb(var(--v-theme-tertiary));
    }

    &:nth-child(3) {
      --icon-color: #eca1c2;
    }

    &:nth-child(4) {
      --icon-color: #8d69d5;
    }
  }

  .v-btn {
    justify-self: center;
  }

  .home-title {
    font-size: clamp(24px, 3vw, 42px);
  }
}
</style>

<i18n lang="json">
{
  "fi": {
    "includes": "Sisältää <b>{amount}</b> allekirjoituskrediitin | Sisältää <b>{amount}</b> allekirjoituskrediittiä ",
    "pcs": "kpl",
    "pricing": "Hinnasto",
    "orderLink": "Tilaa kirjautumislinkki",
    "doesNotIncludeVat": "Kaikki hinnat ALV 0%",
    "includesVat": "Kaikki hinnat sisältävät arvonlisäveron",
    "incVat": "sis. ALV",
    "noVat": "ALV 0%",
    "consumerPricing": "Hinnat kuluttajille",
    "companyPricing": "Hinnat yrittäjille",
    "showCompanyPricing": "Näytä hinnat yrittäjille",
    "showConsumerPricing": "Näytä hinnat kuluttajille",
    "paymentTerms": "Toimitus- ja maksuehdot"
  },
  "en": {
    "includes": "Includes <b>{amount}</b> signature credit | Includes <b>{amount}</b> signature credits",
    "pcs": "pc",
    "pricing": "Pricing",
    "orderLink": "Request a login link",
    "doesNotIncludeVat": "Prices exclude VAT",
    "includesVat": "All prices include VAT",
    "incVat": "inc. VAT",
    "noVat": "VAT 0%",
    "consumerPricing": "Consumer pricing",
    "companyPricing": "Company pricing",
    "showCompanyPricing": "Show company pricing",
    "showConsumerPricing": "Show consumer pricing",
    "paymentTerms": "Terms of payment and delivery"
  }
}
</i18n>
